/**
 * Modal DOM creation utilities
 */

import type { SDKConfig } from '../types';
import { SDKError } from '../types';

export interface ModalElements {
  overlayElement: HTMLDivElement;
  modalElement: HTMLDivElement;
  iframeElement: HTMLIFrameElement;
  formElement: HTMLFormElement;
  iframeName: string;
}

export interface CreateModalOptions {
  config: Required<Pick<SDKConfig, 'timeout' | 'debug'>> & {
    modalContainer?: string;
    modalWidth?: number | 'fullscreen' | undefined;
    modalHeight?: number | 'fullscreen' | undefined;
    customClass?: string;
  };
  enableCloseBtn: boolean;
  getClassName: (baseName: string) => string;
  onClose: () => void;
  onIframeLoad: () => void;
  onIframeError: () => void;
  log?: (message: string, ...args: unknown[]) => void;
}

/**
 * Creates the modal HTML structure
 */
export function createModalDOM(options: CreateModalOptions): ModalElements {
  const { config, enableCloseBtn } = options;
  const { onClose, onIframeLoad, onIframeError, log, getClassName } = options;

  // Create overlay
  const overlayElement = document.createElement('div');
  overlayElement.className = getClassName('tds-modal-overlay');
  overlayElement.setAttribute('role', 'dialog');
  overlayElement.setAttribute('aria-modal', 'true');
  overlayElement.setAttribute('aria-label', '3DS Authentication');

  // Close modal when clicking on overlay (but not on modal itself)
  overlayElement.onclick = (e): void => {
    if (e.target === overlayElement) {
      // Overlay click handling disabled
    }
  };

  // Create modal container
  const modalElement = document.createElement('div');
  modalElement.className = getClassName('tds-modal-container');

  // Apply custom class if provided
  if (config.customClass) {
    modalElement.classList.add(config.customClass);
  }

  // Apply modal dimensions
  applyModalDimensions(modalElement, config, getClassName);

  // Create modal header with close button
  const header = document.createElement('div');
  header.className = getClassName('tds-modal-header');

  const closeButton = document.createElement('button');
  closeButton.className = getClassName('tds-modal-close');
  closeButton.setAttribute('aria-label', 'Close authentication modal');
  closeButton.setAttribute('type', 'button');
  closeButton.innerHTML = '×';
  closeButton.onclick = (): void => {
    onClose();
  };

  // Add keyboard support for close button
  closeButton.onkeydown = (e): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
  };

  header.appendChild(closeButton);
  if (enableCloseBtn) {
    modalElement.appendChild(header);
  }

  // Create modal body with iframe
  const body = document.createElement('div');
  body.className = getClassName('tds-modal-body');

  // Create loading indicator
  const loading = document.createElement('div');
  loading.className = getClassName('tds-modal-loading');
  loading.innerHTML = `
    <div class="${getClassName('tds-spinner')}"></div>
    <p>Processing authentication...</p>
  `;
  body.appendChild(loading);

  // Create iframe with unique name
  const iframeName = `tds-auth-iframe-${Date.now()}`;
  const iframeElement = document.createElement('iframe');
  iframeElement.className = getClassName('tds-modal-iframe');
  iframeElement.setAttribute('name', iframeName);
  iframeElement.setAttribute('title', '3DS Authentication');
  iframeElement.setAttribute('frameborder', '0');
  iframeElement.setAttribute('allow', 'payment');
  iframeElement.setAttribute('allowtransparency', 'true');
  iframeElement.setAttribute('allow', 'otp-credentials; payment; clipboard-write;');

  iframeElement.style.display = 'none';

  // Handle iframe load
  iframeElement.onload = (): void => {
    log?.('Iframe loaded');
    loading.style.display = 'none';
    iframeElement.style.display = 'block';
    onIframeLoad();
  };

  // Handle iframe errors
  iframeElement.onerror = (): void => {
    log?.('Iframe error');
    onIframeError();
  };

  body.appendChild(iframeElement);

  // Create hidden form for submission
  const formElement = document.createElement('form');
  formElement.setAttribute('method', 'post');
  formElement.setAttribute('target', iframeName);
  formElement.style.display = 'none';

  body.appendChild(formElement);

  // Assemble modal
  modalElement.appendChild(body);
  overlayElement.appendChild(modalElement);

  // Append to body or custom container
  const container = config.modalContainer
    ? document.querySelector(config.modalContainer)
    : document.body;

  if (!container) {
    throw new SDKError('Modal container not found', 'CONTAINER_NOT_FOUND');
  }

  container.appendChild(overlayElement);

  // Animate in with smooth entrance
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      overlayElement.classList.add(getClassName('tds-modal-show'));
    });
  });

  return {
    overlayElement,
    modalElement,
    iframeElement,
    formElement,
    iframeName,
  };
}

/**
 * Applies modal dimensions based on configuration
 */
export function applyModalDimensions(
  modalElement: HTMLDivElement,
  config: {
    modalWidth?: number | 'fullscreen' | undefined;
    modalHeight?: number | 'fullscreen' | undefined;
  },
  getClassName: (baseName: string) => string
): void {
  const isFullscreen = config.modalWidth === 'fullscreen' || config.modalHeight === 'fullscreen';
  if (isFullscreen) {
    modalElement.classList.add(getClassName('tds-modal-fullscreen'));
  } else if (typeof config.modalWidth === 'number' || typeof config.modalHeight === 'number') {
    // Apply fixed dimensions if at least one is provided
    if (typeof config.modalWidth === 'number') {
      modalElement.style.width = `${config.modalWidth}px`;
      modalElement.style.maxWidth = `${config.modalWidth}px`;
    }
    if (typeof config.modalHeight === 'number') {
      modalElement.style.height = `${config.modalHeight}px`;
      modalElement.style.maxHeight = `${config.modalHeight}px`;
    }
    // Add responsive class when dimensions are partially or fully undefined
    if (config.modalWidth === undefined || config.modalHeight === undefined) {
      modalElement.classList.add(getClassName('tds-modal-responsive'));
    }
  } else {
    // No dimensions provided - make it fully responsive
    modalElement.classList.add(getClassName('tds-modal-responsive'));
  }
}
